import { useVariant } from '@unleash/proxy-client-react';
import { isValid } from 'date-fns';
import isEmpty from 'lodash/isEmpty';
import type { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';

import {
  fullLogout,
  isRoleRevelioAdmin,
  isTabAllowed,
  ServerProfileTabsPathMap,
  type TabRequirement,
  useAuthStore,
  useGetLoggedInUser,
} from '@revelio/auth';
import { FeatureFlag, LOGIN } from '@revelio/core';
import { Role } from '@revelio/data-access';

interface RequireAuthProps {
  children?: ReactNode;
  tab?: TabRequirement | typeof LOGIN;
  adminRequired?: boolean;
  clientAdminRequired?: boolean;
}
export function RequireAuth({
  children,
  tab = LOGIN,
  adminRequired = false,
  clientAdminRequired = false,
}: RequireAuthProps) {
  const { ory } = useAuthStore();
  const { loggedInUser, realLoggedInUser } = useGetLoggedInUser();
  const realAdminRole = realLoggedInUser?.role;
  let shouldShowChildren = true;

  const session = ory;

  const authenticatedAt = session?.authenticated_at;

  const logoutVariant = useVariant(FeatureFlag.Logout);

  const logoutTimestamp =
    logoutVariant?.name === 'logout-timestamp'
      ? logoutVariant?.payload?.value
      : null;

  if (logoutTimestamp) {
    const forcedSessionExpirationAt = new Date(logoutTimestamp);

    if (
      authenticatedAt &&
      forcedSessionExpirationAt &&
      isValid(forcedSessionExpirationAt) &&
      // if the feature flag is set in the future, it'll keep logging out users
      forcedSessionExpirationAt < new Date() &&
      forcedSessionExpirationAt > new Date(authenticatedAt)
    ) {
      fullLogout();
      return;
    }
  }

  const user = loggedInUser;
  const userTabs = user?.tabs || [];
  const isAdmin =
    isRoleRevelioAdmin(user?.role) || isRoleRevelioAdmin(realAdminRole);
  const isClientAdmin = user?.role === Role.ClientAdmin;

  const allowedTab = isTabAllowed(tab, userTabs);
  if (
    !isEmpty(user) &&
    ((adminRequired && !clientAdminRequired && !isAdmin) ||
      (clientAdminRequired && !isAdmin && !isClientAdmin) ||
      (userTabs.length && !allowedTab))
  ) {
    shouldShowChildren = false;
  }

  return shouldShowChildren && children ? (
    children
  ) : (
    <Navigate to={ServerProfileTabsPathMap.landing} />
  );
}
